import { useMemo, useRef, useState } from "react";
import { useRecoilValueLoadable, useSetRecoilState } from "recoil";
// material
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { Icon } from "@iconify/react";
import searchFill from "@iconify/icons-eva/search-fill";

import { useOpenSnackbar } from "@hooks/useSnackbar";
import axios from "@utils/axios";
import { adminMembershipsQuery, adminRefreshTick } from "@states/admin";
import { MembershipGroupType } from "types/admin";
import { MembershipLevel } from "types/auth";
import { LEVEL_LABEL, LEVEL_ORDER } from "./levels";
import MembershipFormDialog from "./MembershipFormDialog";
import MembershipHistoryDialog from "./MembershipHistoryDialog";

function errorText(error: any, fallback: string): string {
  return typeof error?.detail === "string" ? error.detail : fallback;
}

/** 9999 로 끝나는 종료일은 "무기한" 이라는 운영 관례다. */
function periodText(group: MembershipGroupType): string {
  const end = group.endDate?.startsWith("9999") ? "무기한" : group.endDate;
  return `${group.startDate} ~ ${end}`;
}

type Handlers = {
  onHistory: (group: MembershipGroupType) => void;
  onEdit: (group: MembershipGroupType) => void;
  onRemove: (group: MembershipGroupType) => void;
  busyId: number | null;
};

const buildColumns = ({ onHistory, onEdit, onRemove, busyId }: Handlers): GridColDef[] => [
  { field: "name", headerName: "그룹", flex: 1.2, minWidth: 160 },
  {
    field: "level",
    headerName: "등급",
    flex: 0.5,
    minWidth: 100,
    // 문자열 정렬이면 `admin · basic · elite · pro` 가 된다. 사람이 기대하는 것은
    // 낮은 등급 → 높은 등급이므로 표시 순서로 비교한다 (`levels.ts`).
    sortComparator: (a, b) => {
      const rank = (value: MembershipLevel | null) =>
        value ? LEVEL_ORDER[value] : -1; // 등급을 못 읽은 그룹은 맨 앞
      return rank(a as MembershipLevel | null) - rank(b as MembershipLevel | null);
    },
    renderCell: ({ value }) => {
      const level = value as MembershipLevel | null;
      const label = level ? LEVEL_LABEL[level] : null;
      // DB 에 등급이 아닌 값이 들어 있으면 서버가 null 로 준다.
      // 권한 시스템은 모르면 막아야지 터지면 안 된다.
      return label ? (
        <Chip size="small" label={label.text} color={label.color} />
      ) : (
        <Chip size="small" label="알 수 없음" variant="outlined" />
      );
    },
  },
  {
    field: "startDate",
    headerName: "기간",
    flex: 1,
    minWidth: 200,
    // 칸에는 기간 전체를 보여주되 **정렬은 시작일 기준**이다. 문자열이 `YYYY-MM-DD`
    // 라 사전순 = 날짜순이고, "무기한" 같은 표시 문구로 정렬하면 순서가 무너진다.
    renderCell: ({ row }) => (
      <Typography variant="body2" color="text.secondary">
        {periodText(row as MembershipGroupType)}
      </Typography>
    ),
  },
  {
    field: "userCount",
    headerName: "인원",
    type: "number",
    flex: 0.4,
    minWidth: 80,
  },
  {
    field: "actions",
    headerName: "",
    width: 190,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: ({ row }) => {
      const group = row as MembershipGroupType;
      return (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Button size="small" onClick={() => onHistory(group)}>
            이력
          </Button>
          <Button size="small" onClick={() => onEdit(group)}>
            수정
          </Button>
          {/* 사람이 든 그룹은 서버가 막는다. 누를 수 있게 두면 "눌렀는데 안 된다" 가
              되므로 이유를 붙여 비활성화한다. */}
          <Button
            size="small"
            color="error"
            disabled={busyId === group.id || group.userCount > 0}
            title={group.userCount > 0 ? `${group.userCount}명이 속해 있습니다` : ""}
            onClick={() => onRemove(group)}
          >
            삭제
          </Button>
        </Stack>
      );
    },
  },
];

/**
 * 멤버십 그룹 목록.
 *
 * 사람을 그룹에 넣고 빼는 것은 유저 관리 탭에서 한다. 여기서는 그룹 자체를 만들고
 * 고치고 지운다 — 그룹을 고치면 그 안의 **전원에게 한꺼번에** 적용된다.
 *
 * 표는 유저 관리와 **같은 `DataGridPro`** 를 쓴다. 예전에는 순수 `<table>` 이라
 * 같은 화면의 두 탭에서 정렬도 검색도 다르게 동작했다.
 *
 * 정렬·검색은 **전부 클라이언트**다. `GET /api/memberships` 가 페이징 없이 전량을
 * 주기 때문에 그래도 된다. (유저 목록은 서버 페이징이라 사정이 다르다.)
 */
export default function MembershipPanel() {
  const loadable = useRecoilValueLoadable(adminMembershipsQuery);
  const bumpRefresh = useSetRecoilState(adminRefreshTick);
  const openSnackbar = useOpenSnackbar();
  const [editing, setEditing] = useState<MembershipGroupType | "new" | null>(null);
  const [viewing, setViewing] = useState<MembershipGroupType | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [query, setQuery] = useState("");

  // 삼항식을 그대로 두면 매 렌더마다 새 배열이 된다 (스프린트 4·5 에서 두 번 겪었다).
  const groups = useMemo<MembershipGroupType[]>(
    () => (loadable.state === "hasValue" ? loadable.contents : []),
    [loadable.state, loadable.contents],
  );

  const remove = async (group: MembershipGroupType) => {
    setBusyId(group.id);
    try {
      await axios.delete(`memberships/${group.id}`);
      openSnackbar(`${group.name} 을(를) 지웠습니다.`, "success");
    } catch (error: any) {
      openSnackbar(errorText(error, "지우지 못했습니다."), "error");
    } finally {
      setBusyId(null);
      bumpRefresh((tick) => tick + 1);
    }
  };

  // 그룹 이름과 **보이는 등급 이름**으로 찾는다. 사용자는 `elite` 가 아니라
  // "엘리트" 를 입력한다.
  const rows = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return groups;
    return groups.filter((group) => {
      const levelText = group.level ? LEVEL_LABEL[group.level].text : "알 수 없음";
      return (
        group.name.toLowerCase().includes(keyword) || levelText.toLowerCase().includes(keyword)
      );
    });
  }, [groups, query]);

  // `remove` 는 매 렌더 새로 만들어진다 — `useOpenSnackbar()` 가 매번 새 함수를
  // 돌려주기 때문에 `useCallback` 으로도 안정화되지 않는다. 그렇다고 의존성에 넣으면
  // 매 렌더마다 columns 가 새로 만들어져 그리드가 통째로 다시 그려진다
  // (유저 관리에서 같은 문제를 겪었다).
  //
  // ref 로 최신 것을 가리키게 하면 둘 다 피한다 — 오래된 클로저를 잡지도 않고,
  // columns 는 `busyId` 가 바뀔 때만 다시 만들어진다. eslint 도 ref 는 안정적인 값으로
  // 알고 있어서 억제가 필요 없다.
  const removeRef = useRef(remove);
  removeRef.current = remove;

  const columns = useMemo(
    () =>
      buildColumns({
        onHistory: setViewing,
        onEdit: setEditing,
        onRemove: (group) => removeRef.current(group),
        busyId,
      }),
    [busyId],
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
          그룹을 고치면 <b>그 안의 전원에게 한꺼번에</b> 적용됩니다. 사람을 넣고 빼는 것은{" "}
          <b>유저 관리</b> 탭에서 합니다.
        </Alert>
        <Button variant="contained" onClick={() => setEditing("new")}>
          새 그룹
        </Button>
      </Stack>

      {loadable.state === "hasValue" && groups.length === 0 ? (
        <Alert severity="warning">멤버십 그룹이 하나도 없습니다.</Alert>
      ) : (
        <>
          <TextField
            size="small"
            placeholder="그룹 이름 또는 등급"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ maxWidth: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={searchFill} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ width: "100%" }}>
            <DataGridPro
              autoHeight
              rows={rows}
              loading={loadable.state === "loading"}
              columns={columns}
              disableSelectionOnClick
              density="compact"
              getRowId={(row) => row.id}
              hideFooter
              localeText={{ noRowsLabel: "찾는 그룹이 없습니다." }}
            />
          </Box>
        </>
      )}

      <MembershipFormDialog target={editing} onClose={() => setEditing(null)} />
      <MembershipHistoryDialog group={viewing} onClose={() => setViewing(null)} />
    </Stack>
  );
}
